import { playerSearchWithMetaRatings } from "@/api/api";
import Button from "@/components/Button";
import { Tag } from "@/components/Tag";
import { CreditCostMarker } from "@/components/features/Credits/CreditCostMarker";
import IncludeExcludeSection, {
  IncludeExcludeSectionType,
} from "@/components/organisms/SBCFiltersSidebar/SideBarComponents/IncludeExcludeSection";
import { Button as NewButton } from "@/design-system/Button/Button";
import { cross, penIcon, roundGreenCheckmarkIcon } from "@/designSystemIcons";
import useSearch from "@/hooks/useSearch";
import { cn } from "@/lib/utils";
import { SearchedPlayer } from "@/redux/players/SearchedPlayer.type";
import {
  activeFiltersSelector,
  addBestFormationIncludedPlayer,
  defaultSquadBuilderSelector,
  removeBestFormationIncludedPlayer,
} from "@/redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { getUserSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { usesClubPool } from "@repo/shared/redux";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { TopFilterBar } from "../FiltersBar/FiltersBar";

interface BestFormationIncludeSectionOnPitchProps {
  handleClickedBuildSquads: () => void;
  onClose: () => void;
  isMobileAndInInputMode: boolean;
  handleEditIncludedPlayers?: () => void;
}
const BestFormationIncludeSectionOnPitch = ({
  handleClickedBuildSquads,
  onClose,
  isMobileAndInInputMode,
  handleEditIncludedPlayers,
}: BestFormationIncludeSectionOnPitchProps) => {
  const { t } = useTranslation("squadBuilder");
  const { bestFormationIncludedPlayers, scope } = useSelector(
    defaultSquadBuilderSelector,
  );
  const activeFilters = useSelector(activeFiltersSelector);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const { user } = useSelector(getUserSelector);
  const [showSearch, setShowSearch] = useState<
    IncludeExcludeSectionType | undefined
  >(undefined);
  const [searchTerm, setSearchTerm, isSearching, result] = useSearch(
    "",
    (name: string, page: number) =>
      playerSearchWithMetaRatings(name, page, user.platform || "Playstation"),
  ) as [
    string,
    React.Dispatch<React.SetStateAction<string>>,
    boolean,
    SearchedPlayer[],
  ];
  const [showAllIncluded, setShowAllIncluded] = useState(false);
  const dispatch = useDispatch();
  const handleAddPlayer = (player: SearchedPlayer) => {
    dispatch(addBestFormationIncludedPlayer(player));
    setShowSearch(undefined);
    setSearchTerm("");
  };

  const handleRemovePlayer = (resourceId: number) => {
    dispatch(removeBestFormationIncludedPlayer(resourceId));
  };

  return isMobileAndInInputMode ? (
    <>
      <div className="-mx-2">
        <IncludeExcludeSection
          type="include"
          players={bestFormationIncludedPlayers}
          icon={roundGreenCheckmarkIcon}
          showSearch={showSearch}
          setShowSearch={setShowSearch}
          onAdd={handleAddPlayer}
          onRemove={(_, resourceId) => {
            handleRemovePlayer(resourceId);
          }}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isSearching={isSearching}
          result={result}
          showAll={showAllIncluded}
          setShowAll={setShowAllIncluded}
          setShowAllSectionType={() => {}}
          isMobile={isMobile}
          cantIncludePlayers={false}
          handleClickedAdd={() => setShowSearch("include")}
        />
      </div>
    </>
  ) : (
    <div
      className={cn(
        "bg-panel-base w-full flex flex-col h-fit max-w-150 touch-device:w-96 touch-device:rounded-xl touch-device:shadow-soft-dark-bottom",
      )}
    >
      <div className="flex flex-row h-14 bg-panel-middleground px-2.5 items-center justify-between mobile:rounded-t-xl">
        <label className="font-semibold flex flex-row items-center gap-x-1">
          {t("bestFormation.title")}
        </label>
        <button onClick={onClose} className="p-3 -m-1 stroke-dark-tertiary">
          <div className="w-4 h-4">{cross}</div>
        </button>
      </div>
      <div className="relative overflow-y-auto max-h-132">
        <div className="flex flex-col p-4 bg-panel-background gap-y-4">
          <p className="text-dark-tertiary touch-device:text-dark-secondary text-xs touch-device:text-sm">
            {t("bestFormation.description")}
          </p>
          <div className="flex flex-col gap-y-2">
            <div className="flex flex-row justify-between">
              <label className="text-dark-secondary touch-device:text-dark-tertiary text-sm">
                {t("bestFormation.activeFilters")}
              </label>
              <button
                className="text-dark-tertiary p-2 mobile:hidden -m-2"
                onClick={handleEditIncludedPlayers}
              >
                <div className="w-5 h-5">{penIcon}</div>
              </button>
            </div>
            <div className="w-full">
              <TopFilterBar
                filters={activeFilters}
                onClearFilter={() => {}}
                includedPlayers={bestFormationIncludedPlayers.length}
                disabled
              />
            </div>
          </div>
          <div className="flex flex-col">
            {!isMobile && (
              <div className="-m-4">
                <IncludeExcludeSection
                  type="include"
                  players={bestFormationIncludedPlayers}
                  icon={roundGreenCheckmarkIcon}
                  showSearch={showSearch}
                  setShowSearch={setShowSearch}
                  onAdd={handleAddPlayer}
                  onRemove={(_, resourceId) => {
                    handleRemovePlayer(resourceId);
                  }}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  isSearching={isSearching}
                  result={result}
                  showAll={showAllIncluded}
                  setShowAll={setShowAllIncluded}
                  setShowAllSectionType={() => {}}
                  isMobile={isMobile}
                  cantIncludePlayers={false}
                  handleClickedAdd={() => setShowSearch("include")}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        className={cn(
          "flex flex-col gap-y-2 items-center justify-center border-t border-panel-foreground px-4",
          bestFormationIncludedPlayers.length === 11 ? "h-25" : "h-17.5",
        )}
      >
        {bestFormationIncludedPlayers.length === 11 && (
          <span className="text-tiny text-dark-tertiary">
            {t("bestFormation.elevenSelected")}
          </span>
        )}
        <div className="relative inline-flex items-center">
          <NewButton
            variant="primary"
            size="medium"
            onClick={handleClickedBuildSquads}
          >
            {t("build.buildSquads")}
          </NewButton>
          {/* Same build, same charge — this panel's button had no price on it
              at all, which is the mobile half of DEV-3402. Out of flow, since
              the panel's footer is a fixed height. */}
          <CreditCostMarker
            featureId="build-squad-club"
            when={usesClubPool(scope)}
            placement="beside-button"
          />
        </div>
      </div>
    </div>
  );
};

export default BestFormationIncludeSectionOnPitch;
